import React, { useState, useEffect } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import Logo from './Logo';
import { useAuth } from '../contexts/AuthContext';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase-config';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Chip, IconButton, Modal, Box, Typography, List, ListItem, ListItemText, Button as MuiButton, ToggleButtonGroup, ToggleButton, Tooltip } from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MailIcon from '@mui/icons-material/Mail';
import { getFirestore, collection, query, where, getDocs,getDoc, updateDoc, doc, arrayUnion } from 'firebase/firestore';
import LanguageSwitch from '../i18n/LanguageSwitch';
import { useLang, useT } from '../i18n/LanguageContext';

const Navbar = () => {
  const theme = useTheme();
  const { currentUser, role, isPro, isAdmin, previewTier, setPreviewTier } = useAuth();
  const navigate = useNavigate();
  const { dateLocale } = useLang();
  const t = useT();
  const [unreadCount, setUnreadCount] = useState(0);
  const [messages, setMessages] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);

  const db = getFirestore();

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Errore durante il logout:', error);
    }
  };

  // Fetch unread messages count and messages list
  useEffect(() => {
    if (currentUser) {
      const fetchMessages = async () => {
        const q = query(
          collection(db, 'messages'),
          where('recipientIds', 'array-contains', currentUser.uid)
        );

        const allUsersQuery = query(
          collection(db, 'messages'),
          where('recipientIds', '==', [])
        );

        const personalMessagesSnapshot = await getDocs(q);
        const allUsersMessagesSnapshot = await getDocs(allUsersQuery);

        const unreadMessages = [];

        personalMessagesSnapshot.forEach((doc) => {
          if (!doc.data().readBy?.includes(currentUser.uid)) {
            unreadMessages.push({ id: doc.id, ...doc.data() });
          }
        });

        allUsersMessagesSnapshot.forEach((doc) => {
          if (!doc.data().readBy?.includes(currentUser.uid)) {
            unreadMessages.push({ id: doc.id, ...doc.data() });
          }
        });

        setUnreadCount(unreadMessages.length);
        setMessages(unreadMessages);
      };

      fetchMessages();
    }
  }, [currentUser, db]);

  const handleMarkAsRead = async (messageId) => {
    const messageRef = doc(db, 'messages', messageId);

    try {
        // Use arrayUnion to add the current user's UID to the readBy array
        
        await updateDoc(messageRef, {
            readBy: arrayUnion(currentUser.uid),
        });

        // Refresh the messages list
        setMessages((prevMessages) => prevMessages.filter((msg) => msg.id !== messageId));
        setUnreadCount((prevCount) => prevCount - 1);
    } catch (error) {
        console.error('Error marking message as read:', error);
    }
};


  const handleOpenModal = () => setModalOpen(true);
  const handleCloseModal = () => setModalOpen(false);

  // Effective tier: Admin by role; otherwise Pro/Free by entitlement (a paying
  // subscriber keeps role 'Free' but isPro === true).
  const tier = role === 'Admin' ? 'Admin' : (isPro ? 'Pro' : 'Free');
  const tierColor = { Admin: '#d32f2f', Pro: theme.palette.primary.main, Free: '#6b7280' }[tier];

  return (
    <AppBar
      position="static"
      color="default"
      elevation={0}
      sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "center",
          },
        }}
      >
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none', marginTop: 5 }}>
          <Logo height={30} />
        </Link>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
          }}
        >
          {currentUser && (
            <Chip
              label={tier}
              size="small"
              onClick={() => navigate('/profile')}
              sx={{
                mr: 2, fontWeight: 700, letterSpacing: '0.04em', color: '#fff',
                backgroundColor: tierColor, cursor: 'pointer',
                '&:hover': { filter: 'brightness(1.08)' },
              }}
            />
          )}

          <LanguageSwitch sx={{ mr: 2 }} />

          {isAdmin && (
            <Tooltip title={t('nav.previewTooltip')}>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={previewTier || 'Admin'}
                onChange={(e, v) => setPreviewTier(!v || v === 'Admin' ? null : v)}
                sx={{ mr: 2, '& .MuiToggleButton-root': { py: 0.2, px: 1, fontSize: '0.72rem', lineHeight: 1.4 } }}
              >
                <ToggleButton value="Admin">Admin</ToggleButton>
                <ToggleButton value="Pro">Pro</ToggleButton>
                <ToggleButton value="Free">Free</ToggleButton>
              </ToggleButtonGroup>
            </Tooltip>
          )}

          {currentUser && (
            <>
              <IconButton
                sx={{ my: 1, mx: 1.5, color: "inherit" }}
                aria-label={t('nav.messagesAriaLabel')}
                onClick={handleOpenModal}
              >
                <Badge badgeContent={unreadCount} color="secondary">
                  <NotificationsIcon />
                </Badge>
              </IconButton>

              <Modal
                open={modalOpen}
                onClose={handleCloseModal}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    border: '2px solid #000',
                    boxShadow: 24,
                    p: 4,
                  }}
                >
                  <Typography id="modal-title" variant="h6" component="h2">
                    {t('nav.messagesModalTitle')}
                  </Typography>
                  <List>
                    {messages.map((message) => (
                      <ListItem key={message.id} alignItems="flex-start">
                        <ListItemText
                          primary={message.subject}
                          secondary={
                            <>
                              <Typography
                                sx={{ display: 'inline' }}
                                component="span"
                                variant="body2"
                                color="text.primary"
                              >
                                {message.body}
                              </Typography>
                              {" — " + new Date(message.timestamp.seconds * 1000).toLocaleString(dateLocale)}
                            </>
                          }
                        />
                        <MuiButton
                          size="small"
                          color="primary"
                          onClick={() => handleMarkAsRead(message.id)}
                        >
                          {t('nav.markAsRead')}
                        </MuiButton>
                      </ListItem>
                    ))}
                  </List>
                  <MuiButton onClick={handleCloseModal} sx={{ mt: 2 }} variant="contained">{t('nav.close')}</MuiButton>
                </Box>
              </Modal>
            </>
          )}

          <Button
            component={Link}
            to="/"
            sx={{ my: 1, mx: 1.5, color: "inherit" }}
          >
            {t('nav.home')}
          </Button>

          <Button
            component={Link}
            to="/metodo"
            sx={{ my: 1, mx: 1.5, color: "inherit" }}
          >
            {t('nav.method')}
          </Button>

          <Button
            component={Link}
            to="/piattaforma"
            sx={{ my: 1, mx: 1.5, color: "inherit" }}
          >
            {t('nav.tool')}
          </Button>

          {currentUser ? (
            <div>
              <Button
                component={Link}
                to="/dashboard"
                sx={{ my: 1, mx: 1.5, color: "inherit" }}
              >
                {t('nav.dashboard')}
              </Button>

              <Button
                component={Link}
                to="/profile"
                startIcon={<AccountCircleIcon />}
                sx={{ my: 1, mx: 1.5, color: "inherit" }}
              >
                {t('nav.profile')}
              </Button>

              {/* Admin section button, visible only to admin users */}
              {role === 'Admin' && (
                <Button
                  component={Link}
                  to="/admin"
                  sx={{ my: 1, mx: 1.5, color: "inherit" }}
                >
                  {t('nav.adminLink')}
                </Button>
              )}
              {/* Admin section button, visible only to admin users */}
              {role === 'Admin' && (
                <Button
                  component={Link}
                  to="/admin/announcements"
                  sx={{ my: 1, mx: 1.5, color: "inherit" }}
                >
                  <MailIcon />
                </Button>
              )}

              <span>{currentUser.email}</span>
              <Button onClick={handleLogout}>{t('nav.logout')}</Button>
            </div>
          ) : (
            <Button
              component={Link}
              to="/login"
              variant="contained"
              sx={{ my: 1, mx: 1.5, color: "white" }}
            >
              {t('nav.login')}
            </Button>
          )}
        </div>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
